import { useEffect } from "react";
import { Button, Form, Input, Modal } from "antd";
import CIQDatePicker from "components/custom-date-picker";
import type { Dayjs } from "dayjs";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import { type AddMilestonePlacement } from "../../design-package-milestone-update";

export type AddMilestoneValues = {
  name: string;
  isoDate: string;
};

export default function AddDesignPackageMilestoneModal(props: {
  open: boolean;
  placement: AddMilestonePlacement | null;
  existingNames: string[];
  confirmLoading: boolean;
  onClose: () => void;
  onConfirm: (values: AddMilestoneValues) => void;
}) {
  const { open, placement, existingNames, confirmLoading, onClose, onConfirm } =
    props;
  const [form] = Form.useForm<{
    name: string;
    date: Dayjs | null;
  }>();

  useEffect(() => {
    if (open && placement) {
      form.setFieldsValue({
        name: "",
        date: null
      });
    }
  }, [open, placement, form]);

  const takenNames = new Set(existingNames.map((n) => n.trim().toLowerCase()));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      title="Add Milestone"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            disabled={confirmLoading}
            data-testid="add-ms-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={confirmLoading}
            data-testid="add-ms-confirm"
            onClick={() => form.submit()}
          >
            + Add Milestone
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        data-testid="add-ms-form"
        onFinish={(values) => {
          if (!values.date) return;
          const isoDate = DateUtils.formatDateWithLunchTime(values.date);
          if (!isoDate) return;
          onConfirm({
            name: values.name.trim(),
            isoDate
          });
        }}
      >
        <Form.Item
          label="Milestone Name"
          name="name"
          rules={[
            { required: true, whitespace: true, message: "Enter a name" },
            {
              validator: async (_, value: string) => {
                if (value && takenNames.has(value.trim().toLowerCase())) {
                  throw new Error("A milestone with that name already exists");
                }
              }
            }
          ]}
        >
          <Input data-testid="add-ms-name" placeholder="Enter Milestone Name" />
        </Form.Item>
        <Form.Item
          label="Committed Date"
          name="date"
          rules={[
            { required: true, message: "Pick a date" },
            {
              validator: async (_, value: Dayjs | null) => {
                const latestActual = placement?.latestActualDate;
                if (!value || !latestActual) return;
                if (value.format("YYYY-MM-DD") < latestActual) {
                  throw new Error(
                    "Committed date cannot be before the last actual date"
                  );
                }
              }
            }
          ]}
        >
          <CIQDatePicker
            data-testid="add-ms-date"
            className="w-full"
            format={DATE_FORMAT_MMDDYYYY}
            placeholder="MM-DD-YYYY"
            allowClear={false}
            style={{ width: "100%" }}
            disabledDate={(d) => {
              const latestActual = placement?.latestActualDate;
              if (!latestActual) return false;
              return d.format("YYYY-MM-DD") < latestActual;
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
